import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_STORAGE_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

function checkWasDismissedRecently(): boolean {
  try {
    const dismissed = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!dismissed) return false;
    const dismissedTime = parseInt(dismissed, 10);
    return Date.now() - dismissedTime < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

// iOS Safari never fires `beforeinstallprompt` — Apple has never implemented it.
// Install is only possible via Safari's Share → Add to Home Screen, and only in
// real Safari (not Chrome/Firefox/Edge on iOS, which all use WebKit but lack the
// install affordance, and not in-app webviews like Gmail/Slack/Mail link previews).
//
// UA sniffing is fragile. The exclusion list errs toward false-negatives — we'd
// rather not show the banner to someone who *could* install than mislead someone
// who can't. SFSafariViewController shares Safari's UA and JS environment by
// Apple's deliberate design (anti-fingerprinting); there is no synchronous JS
// signal that distinguishes it. The banner's secondary line ("Don't see Share?
// Open this page directly in Safari") is the mitigation for that case.
const NON_SAFARI_IOS_BROWSERS = /CriOS|FxiOS|EdgiOS|EdgA|OPiOS|OPT\/|OPX\/|Focus\/|YaBrowser|UCBrowser|DuckDuckGo|Brave|SamsungBrowser|Chrome\//;
const IOS_IN_APP_WEBVIEWS = /FBAN|FBAV|FB_IAB|Instagram|GSA\/|LinkedInApp|Line\/|MicroMessenger|Pinterest|Snapchat|Twitter|wv\)/;

function detectIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ in desktop-site mode reports as MacIntel. navigator.platform is
  // deprecated, so also accept touch-capable Macs via `ontouchend` as a fallback
  // (real Macs report maxTouchPoints === 0, even on touch-bar models).
  const isIOS = /iPad|iPhone|iPod/.test(ua)
    || ((navigator.platform === 'MacIntel' || 'ontouchend' in document)
        && navigator.maxTouchPoints > 1
        && !/Android/.test(ua));
  if (!isIOS) return false;
  if (!/Safari\//.test(ua)) return false;
  if (NON_SAFARI_IOS_BROWSERS.test(ua)) return false;
  if (IOS_IN_APP_WEBVIEWS.test(ua)) return false;
  return true;
}

function detectStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
}

export function usePWAInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOSInstallable, setIsIOSInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [wasDismissed, setWasDismissed] = useState(() => checkWasDismissedRecently());

  useEffect(() => {
    if (detectStandalone()) {
      setIsInstalled(true);
      return;
    }

    if (detectIOSSafari()) {
      setIsIOSInstallable(true);
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setIsIOSInstallable(false);
      setInstallPrompt(null);
    };

    // iOS Safari fires no `appinstalled` and the originating tab keeps running
    // after Add to Home Screen. On return to the tab, re-check standalone and
    // re-read the dismissal record (a long-lived tab may have crossed the 24h
    // boundary). Mirrors the pattern in usePWAUpdate.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (detectStandalone()) {
        setIsInstalled(true);
        return;
      }
      setWasDismissed(checkWasDismissedRecently());
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const install = useCallback(async () => {
    if (!installPrompt) {
      return false;
    }

    try {
      // Show the install prompt
      await installPrompt.prompt();

      // Wait for user choice
      const { outcome } = await installPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
      }

      // Clear the saved prompt - it can only be used once
      setInstallPrompt(null);

      return outcome === 'accepted';
    } catch (error) {
      console.error('PWA install prompt failed:', error);
      setInstallPrompt(null);
      return false;
    }
  }, [installPrompt]);

  const dismiss = useCallback(() => {
    setIsInstallable(false);
    setIsIOSInstallable(false);
    setInstallPrompt(null);
    setWasDismissed(true);
    // Store dismissal in localStorage to not show again for 24 hours
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
    } catch (e) {
      console.warn('Failed to persist PWA dismissal:', e);
    }
  }, []);

  return {
    isInstallable: isInstallable && !wasDismissed,
    isIOSInstallable: isIOSInstallable && !wasDismissed,
    isInstalled,
    install,
    dismiss,
  };
}
